import {
  type MantineThemeOverride,
  MantineProvider as Provider,
  createTheme,
  type MantineThemeColorsOverride,
  v8CssVariablesResolver,
  rem,
} from '@mantine/core';
import {
  Global,
  MantineEmotionProvider,
  emotionTransform,
} from '@mantine/emotion';
import { Notifications } from '@mantine/notifications';
import { type ReactNode, useMemo } from 'react';
import { useV2UI } from '../hooks';
import { theme as defaultTheme } from './theme';

// import inter font
import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/inter/900.css';

// import mantine styles
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css'; //if using mantine date picker
import '@mantine/schedule/styles.css';
import '@mantine/notifications/styles.css';

type Colors = MantineThemeColorsOverride['colors'];

export type ThemeColor = keyof Colors;

export const RootThemeProvider = ({
  children,
  themePrimaryColor,
  colorScheme,
  respectReducedMotion = false,
}: {
  children: ReactNode;
  themePrimaryColor: keyof Colors;
  colorScheme?: 'light' | 'dark';
  respectReducedMotion?: boolean;
}) => {
  const { isDarkTheme } = useV2UI();

  const theme = useMemo<MantineThemeOverride>(() => {
    return createTheme({
      ...defaultTheme,
      primaryColor: themePrimaryColor || defaultTheme.primaryColor,
      respectReducedMotion,
    });
  }, [respectReducedMotion, themePrimaryColor]);

  const forceColorScheme = useMemo(() => {
    if (colorScheme) return colorScheme;
    return isDarkTheme ? 'dark' : 'light';
  }, [colorScheme, isDarkTheme]);

  return (
    <Provider
      theme={theme}
      cssVariablesResolver={v8CssVariablesResolver}
      forceColorScheme={forceColorScheme}
      stylesTransform={emotionTransform}
      withGlobalClasses
    >
      <MantineEmotionProvider>
        {children}
        <Global
          styles={{
            '#sentry-feedback': {
              zIndex: 100,
            },
            '.mantine-Table-td, .mantine-Table-th': {
              // Optically center content without changing the total row padding.
              paddingTop: 'calc(var(--table-vertical-spacing) + 1px)',
              paddingBottom: 'calc(var(--table-vertical-spacing) - 1px)',
            },
            'tfoot tr': {
              borderBottom: 'none!important',
            },
          }}
        />
        <Notifications
          className="mp-mask"
          pauseResetOnHover="notification"
          sx={(theme) => ({
            // Keep toasts above the fixed bottom ActionBar and the advanced deposits modal footer and selection bar so their buttons stay clickable.
            '&[data-position^="bottom"]': { bottom: rem(124) },
            '> div': {
              display: 'flex',
              gap: theme.spacing.xs,
              flexDirection: 'column',
            },
          })}
        />
      </MantineEmotionProvider>
    </Provider>
  );
};
